/**
 * One account.
 *
 * Shows the account fields an administrator may need when answering a support
 * question, plus the three actions available here: activating without a code,
 * suspending, and lifting a suspension.
 *
 * Suspension offers "permanent" as a preset number of days; the server stores one
 * expiry either way, so nothing downstream needs to know which button was used.
 */

import { useState } from 'react'
import { Link, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import { BAN_PERMANENT_DAYS } from '@shared/constants'

import { ErrorAlert, ErrorBlock, LoadingBlock } from '../../components/layout/states'
import { Button } from '../../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import { Chip, ChipGroup } from '../../components/ui/chip'
import { Input } from '../../components/ui/input'
import { Stat, StatGrid } from '../../components/ui/primitives'
import {
  useActivateUser,
  useAdminUser,
  useBanUser,
  useUnbanUser,
} from '../../hooks/use-admin'
import { formatUnixSeconds, joinList } from '../../lib/format'
import { currentLocale } from '../../lib/i18n'
import { AdminPage, DetailRow, StateBadge, adminErrorText, isBanned } from './shared'

/** Durations offered as presets; the custom field accepts any positive number. */
const BAN_PRESETS = [1, 7, 30, 3650] as const

export function AdminUserDetailPage() {
  const { t } = useTranslation()
  const { id = '' } = useParams()
  const locale = currentLocale()

  const detail = useAdminUser(id)
  const activate = useActivateUser()
  const ban = useBanUser()
  const unban = useUnbanUser()

  const [days, setDays] = useState('7')
  const [reason, setReason] = useState('')

  // Read as text rather than as an `ApiError`: a request that never reached the
  // server still has to say something instead of leaving the page silent.
  const errorText = adminErrorText(activate.error ?? ban.error ?? unban.error, t)

  const user = detail.data?.user
  const banned = isBanned(user?.bannedUntil)

  return (
    <AdminPage
      title={t('admin.user.title')}
      description={user?.email}
      current={t('admin.nav.users')}
    >
      {detail.isPending ? <LoadingBlock label={t('common.loading')} /> : null}
      {detail.isError ? (
        <ErrorBlock
          title={t('admin.common.error')}
          retryLabel={t('admin.common.retry')}
          onRetry={() => void detail.refetch()}
        />
      ) : null}

      {errorText ? (
        <ErrorAlert message={errorText} />
      ) : null}

      {user ? (
        <>
          <Card>
            <CardHeader>
              <CardTitle>{t('admin.user.account')}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col">
              <DetailRow label={t('admin.users.email')}>{user.email}</DetailRow>
              <DetailRow label={t('admin.users.nickname')}>
                {user.nickname ?? t('admin.users.noNickname')}
              </DetailRow>
              <DetailRow label={t('admin.users.registeredAt')}>
                {formatUnixSeconds(user.createdAt, locale)}
              </DetailRow>
              <DetailRow label={t('admin.users.loginMethods')}>
                {joinList(
                  user.loginMethods.map((method) => t(`admin.users.loginMethod.${method}`)),
                  t,
                )}
              </DetailRow>
              <DetailRow label={t('admin.users.referralCode')}>{user.referralCode}</DetailRow>
              <DetailRow label={t('admin.users.referrer')}>
                {user.referrerEmail ?? t('admin.users.noReferrer')}
              </DetailRow>
              <DetailRow label={t('admin.users.referralChannel')}>
                {user.referralChannel ? t(`admin.referralChannel.${user.referralChannel}`) : '—'}
              </DetailRow>
              <DetailRow label={t('admin.users.state')}>
                <span className="flex flex-wrap items-center gap-2">
                  <StateBadge
                    label={user.isInvited ? t('admin.users.activated') : t('admin.users.notActivated')}
                    variant={user.isInvited ? 'success' : 'outline'}
                  />
                  {user.isAdmin ? (
                    <StateBadge label={t('admin.users.administrator')} variant="default" />
                  ) : null}
                  {banned ? (
                    <StateBadge label={t('admin.users.suspended')} variant="destructive" />
                  ) : null}
                </span>
              </DetailRow>
              {user.bannedUntil ? (
                <DetailRow label={t('admin.users.bannedUntil')}>
                  {formatUnixSeconds(user.bannedUntil, locale)}
                </DetailRow>
              ) : null}
              {user.banReason ? (
                <DetailRow label={t('admin.users.banReason')}>{user.banReason}</DetailRow>
              ) : null}
            </CardContent>
          </Card>

          <StatGrid>
            <Stat label={t('admin.user.createdEvents')} value={detail.data?.stats.createdEvents ?? 0} />
            <Stat label={t('admin.user.joinedEvents')} value={detail.data?.stats.joinedEvents ?? 0} />
            <Stat
              label={t('admin.user.referredSignups')}
              value={detail.data?.stats.referredSignups ?? 0}
            />
          </StatGrid>

          <Card>
            <CardHeader>
              <CardTitle>{t('admin.user.actions')}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              {!user.isInvited ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="self-start"
                  disabled={activate.isPending}
                  onClick={() => activate.mutate(user.id)}
                >
                  {t('admin.user.activate')}
                </Button>
              ) : null}

              {banned ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="self-start"
                  disabled={unban.isPending}
                  onClick={() => unban.mutate(user.id)}
                >
                  {t('admin.user.unban')}
                </Button>
              ) : (
                <>
                  <ChipGroup label={t('admin.user.banDays')}>
                    {BAN_PRESETS.map((preset) => (
                      <Chip
                        key={preset}
                        selected={Number(days) === preset}
                        type="button"
                        onClick={() => setDays(String(preset))}
                      >
                        {preset === BAN_PERMANENT_DAYS
                          ? t('admin.user.banPermanent')
                          : t('admin.user.banDaysOption', { days: preset })}
                      </Chip>
                    ))}
                  </ChipGroup>
                  <Input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    value={days}
                    aria-label={t('admin.user.banDays')}
                    onChange={(event) => setDays(event.target.value)}
                  />
                  <Input
                    value={reason}
                    maxLength={200}
                    placeholder={t('admin.user.banReasonPlaceholder')}
                    aria-label={t('admin.user.banReason')}
                    onChange={(event) => setReason(event.target.value)}
                  />
                  <Button
                    variant="destructive"
                    size="sm"
                    className="self-start"
                    disabled={ban.isPending || Number(days) <= 0}
                    onClick={() => {
                      const duration = Number(days)
                      // The permanent preset travels as the flag the server
                      // understands, so the two entry points stay distinguishable
                      // in the request that reaches the audit-less logs.
                      ban.mutate({
                        id: user.id,
                        ...(duration === BAN_PERMANENT_DAYS
                          ? { permanent: true }
                          : { days: duration }),
                        ...(reason.trim() ? { reason: reason.trim() } : {}),
                      })
                    }}
                  >
                    {t('admin.user.ban')}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          <div>
            <Button asChild variant="outline" size="sm">
              <Link to="/admin/users">{t('admin.users.back')}</Link>
            </Button>
          </div>
        </>
      ) : null}
    </AdminPage>
  )
}
